import * as React from "react";
import clsx from "clsx";
import { GoogleIcon } from "../../../ui/components/Auth/GoogleIcon";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import {
  isUsingGoogleAvatar,
  isValidAvatarUrl,
  normalizeAvatarUrl,
} from "../../lib/profile-avatar-image";
import { uploadAvatarFile, uploadAvatarFromUrl } from "../../lib/profile-avatar-upload";
import { useI18n } from "../../lib/i18n";
import * as styles from "./ProfileAvatarEditor.css";

export interface ProfileAvatarEditorProps {
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  googleAvatarUrl?: string | null;
  onAvatarUrlChange: (url: string) => void;
  onAvatarPersist?: (url: string) => void | Promise<void>;
  accessToken?: string | null;
  changed?: boolean;
}

export function ProfileAvatarEditor({
  email,
  firstName,
  lastName,
  avatarUrl,
  googleAvatarUrl,
  onAvatarUrlChange,
  onAvatarPersist,
  accessToken,
  changed,
}: ProfileAvatarEditorProps) {
  const { t } = useI18n();
  const [error, setError] = React.useState<string | null>(null);

  const googleStored = normalizeAvatarUrl(googleAvatarUrl);
  const currentUrl = normalizeAvatarUrl(avatarUrl);
  const showGoogleBadge = isUsingGoogleAvatar(avatarUrl, googleAvatarUrl);
  const canResetToGoogle = !!googleStored && googleStored !== currentUrl;

  const items: MediaEntry[] = currentUrl ? [{ id: "current", url: currentUrl }] : [];

  const handleUploadFile = React.useCallback(async (file: File): Promise<string> => {
    if (!accessToken) throw new Error("Sign in again to upload an image.");
    return uploadAvatarFile(accessToken, file);
  }, [accessToken]);

  const handleApply = React.useCallback(async (newItems: MediaEntry[]) => {
    const trimmed = normalizeAvatarUrl(newItems[0]?.url ?? "");
    if (trimmed && !isValidAvatarUrl(trimmed)) {
      throw new Error("Enter a valid image URL (http or https).");
    }
    if (!accessToken) {
      throw new Error("Sign in again to update your avatar.");
    }

    let finalUrl = trimmed;

    // MediaUploader only knows how to upload local files (uploadFile already ran for
    // those); a pasted external URL still needs to be re-hosted here, same as before.
    const isAlreadyHosted = !finalUrl || finalUrl === googleStored || finalUrl === currentUrl;

    if (finalUrl && !isAlreadyHosted) {
      finalUrl = await uploadAvatarFromUrl(accessToken, finalUrl);
    }

    onAvatarUrlChange(finalUrl);
    await onAvatarPersist?.(finalUrl);
  }, [accessToken, googleStored, currentUrl, onAvatarUrlChange, onAvatarPersist]);

  const handleRemove = React.useCallback(() => {
    onAvatarUrlChange("");
    setError(null);
    void (async () => {
      try {
        await onAvatarPersist?.("");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to remove profile image");
      }
    })();
  }, [onAvatarUrlChange, onAvatarPersist]);

  return (
    <div className={clsx(styles.avatarControl, changed && styles.avatarControlChanged)}>
      <MediaUploader
        mediaType="image"
        items={items}
        onChange={() => {}}
        onApply={handleApply}
        uploadFile={handleUploadFile}
        maxItems={1}
        shape="circle"
        previewSize={80}
        badge={showGoogleBadge ? (
          <span
            className={authText.avatarGoogleStatusBadge}
            style={{ width: 30, height: 30 }}
            title={t("pro.profile.googleProfilePhoto")}
            aria-label={t("pro.profile.googleProfilePhoto")}
          >
            <GoogleIcon size={24} />
          </span>
        ) : undefined}
        extraActions={canResetToGoogle ? [
          {
            label: "Reset to Google avatar",
            previewUrl: googleStored,
            onClick: () => {},
          },
        ] : undefined}
        onRemove={currentUrl ? handleRemove : undefined}
        error={error}
        onError={setError}
      />
    </div>
  );
}
